// Members' Golf Game Central — login + member-owned game data, on the shared
// completetourneylive backend (same /ggGames + /memberGames the companion app
// uses). Strictly mirrors the app's model (PHASE0.md): member-ID login via the
// SAME memberAuth allowlist function, member-owned saves, own-games-only reads.
import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken, onAuthStateChanged, signOut, GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth'
import { ref, push, set, onValue, get, update, remove } from 'firebase/database'
import { memberDb, memberAuth, memberFunctions } from './memberBackend.js'
import { stripUndefinedDeep } from '../admin/gg/ggGamesSanitize.js'
import { HUSTLER } from '../config/siteMode.js'

const CLUB_ID = 'pbcc'
// Dedicated per-BRAND tag — clubId is the golf club (Pine Bluff), NOT the brand, so
// it can't separate PBCC vs Golf Hustler. ggc/pbcc build → 'pbcc', hustler build →
// 'hustler'. Every created/re-sent game is stamped with this, and watchMyGames filters
// on it, so a game shows ONLY in its own brand's site + app. Must match each app's
// COMPANION_BRAND exactly. A record with no brand is treated as own (legacy stays).
const BRAND = HUSTLER ? 'hustler' : 'pbcc'

// ── Login (member ID number → memberAuth allowlist → custom-token sign-in) ──
export async function memberLogin(memberId) {
  const id = String(memberId || '').trim()
  if (!/^\d{1,20}$/.test(id)) return { ok: false, reason: 'invalid' }
  let data
  try {
    data = (await httpsCallable(memberFunctions, 'memberAuth')({ memberId: id })).data
  } catch {
    return { ok: false, reason: 'network' }
  }
  if (!data?.ok) return { ok: false, reason: data?.reason === 'invalid' ? 'invalid' : 'not-a-member' }
  try {
    await signInWithCustomToken(memberAuth, data.token)
  } catch {
    return { ok: false, reason: 'auth-failed' }
  }
  return { ok: true, memberId: data.memberId, name: data.name ?? null }
}

export function getMemberId() { return memberAuth.currentUser?.uid ?? null }
export function onMemberChange(cb) { return onAuthStateChanged(memberAuth, (u) => cb(u?.uid ?? null)) }
export async function memberLogout() { await signOut(memberAuth) }

// ── Sign in with Apple / Google (Firebase Auth OAuth) ────────────────────────
// The user's Apple/Google account IS their account: uid = the provider uid, so
// getMemberId() returns it and ALL member-owned reads/writes above key to that
// uid unchanged (same /ggGames + /memberGames the companion app uses). Requires
// the Apple + Google providers enabled in Firebase Console (see report).
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  try { const r = await signInWithPopup(memberAuth, provider); return { ok: true, uid: r.user.uid } }
  catch (e) { return { ok: false, reason: e?.code || 'auth-failed' } }
}
export async function signInWithApple() {
  const provider = new OAuthProvider('apple.com')
  provider.addScope('email'); provider.addScope('name')
  try { const r = await signInWithPopup(memberAuth, provider); return { ok: true, uid: r.user.uid } }
  catch (e) { return { ok: false, reason: e?.code || 'auth-failed' } }
}
// Display identity for the signed-in member bar (name/email/photo, no uid leak).
export function getMemberProfile() {
  const u = memberAuth.currentUser
  return u ? { uid: u.uid, name: u.displayName || null, email: u.email || null } : null
}

const idxRef = (uid, gid) => ref(memberDb, `memberGames/${uid}${gid ? '/' + gid : ''}`)

// ── Member-owned save — writes the FULL Wolf contract record (the SAME meta +
// players ggGamesData.createGame builds — options, addons.scats/skins/dots,
// course, per-player tees/HC#/allowance/courseHandicap — plus the ownership
// wrapper) to /ggGames, and the /memberGames/{uid} index keyed to the member's
// ID. Takes (meta, players), identical to createGame, so the setup form feeds
// it unchanged. Play data (scores/wolf/wolfBet/dots) accretes onto the same
// record during the round; the companion reads /ggGames/{id} to sync.
export async function saveMemberGame(meta, players) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const now = Date.now()
  const rec = stripUndefinedDeep({
    ownerId: uid,
    clubId: CLUB_ID,
    brand: BRAND,
    status: 'active',
    origin: 'site',
    sharedWith: {},
    createdAt: now,
    updatedAt: now,
    meta: { ...meta, createdAt: now, status: 'active' },
    players: players || [],
    scores: {},
  })
  const node = push(ref(memberDb, 'ggGames'))
  const gameId = node.key
  await set(node, rec)
  await set(idxRef(uid, gameId), {
    // hiddenApp:false — always visible in the app on save/send (never carry a stale hide).
    role: 'owner', name: meta?.name || meta?.gameId || 'Game', gameId: meta?.gameId || null, status: 'active', updatedAt: now, hiddenApp: false,
  })
  return gameId
}

// ── Brand separation ──────────────────────────────────────────────────────────
// /memberGames/{uid} is SHARED across brands (same per-user uid on the same backend)
// and the index entry has no brand — only the /ggGames record does. Keep only THIS
// build's BRAND by reading each game's /ggGames/{id}/brand (cached — it never changes).
// A missing brand is treated as own so legacy games stay; the other brand's games
// (which carry their own brand) are filtered out.
const brandCache = new Map()
async function keepOwnBrand(entries) {
  const brands = await Promise.all(entries.map(async ([id]) => {
    let b = brandCache.get(id)
    if (b === undefined) {
      try { b = (await get(ref(memberDb, `ggGames/${id}/brand`))).val() ?? null }
      catch { b = null }
      brandCache.set(id, b)
    }
    return b
  }))
  return entries.filter((_, i) => (brands[i] ?? BRAND) === BRAND)
}

// ── Own games only — reads /memberGames/{uid} (rules enforce own-only) ──
export function watchMyGames(cb) {
  const uid = getMemberId()
  if (!uid) { cb([]); return () => {} }
  return onValue(idxRef(uid), (s) => {
    const v = s.val() || {}
    // WEBSITE list: hide entries the website soft-deleted (hiddenWeb); hiddenApp is
    // ignored here so an app-only delete stays visible on the website. Then keep ONLY
    // this brand's games — the index is shared with the other brand's site + app.
    const entries = Object.entries(v).filter(([, r]) => !(r && r.hiddenWeb === true))
    keepOwnBrand(entries)
      .then((own) => cb(own.map(([id, r]) => ({ id, ...(r || {}) })).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))))
      .catch(() => cb([]))
  })
}

export async function loadMemberGame(id) {
  const s = await get(ref(memberDb, `ggGames/${id}`))
  return s.val() || null
}

// Delete a saved game from the member's account — PER-SURFACE soft hide, independent
// from the app. The WEBSITE delete sets hiddenWeb=true on the owner index entry and
// KEEPS the shared /ggGames record, so live score-sync and the APP keep working. Only
// when the app has ALREADY hidden it (hiddenApp===true) do we hard-remove both nodes —
// both sides deleted → the game is truly gone. watchMyGames is live, so the list
// refreshes on its own.
export async function deleteMemberGame(id) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const entry = (await get(idxRef(uid, id))).val() || {}
  if (entry.hiddenApp) {
    await remove(ref(memberDb, `ggGames/${id}`))
    await remove(idxRef(uid, id))
    return
  }
  await update(idxRef(uid, id), { hiddenWeb: true })
}

// ── Re-send an EXISTING game (same /ggGames/{id}) — always a FRESH game ────────
// The website's only job is to send a game to the app to PLAY, so it never carries
// play progress. A re-send writes the SETUP ONLY (meta + players) and CLEARS all
// play/progress: scores, Wolf picks, dots, and wolfBet (overrides/multipliers/
// presses) are wiped and status/meta.status reset to 'active'. The game reappears in
// the app on hole 1 with no money, regardless of anything played before. createdAt is
// preserved (same game, not a duplicate). Refreshes the /memberGames index.
export async function updateMemberGame(id, meta, players) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const now = Date.now()
  const prevMeta = (await get(ref(memberDb, `ggGames/${id}/meta`))).val() || {}
  await update(ref(memberDb, `ggGames/${id}`), stripUndefinedDeep({
    meta: { ...meta, createdAt: prevMeta.createdAt || now, status: 'active' },
    players: players || [],
    status: 'active',
    brand: BRAND,   // (re)stamp the sending site's brand — brands legacy games on re-send
    scores: null,   // clear play progress — RTDB removes the node (app reads {} )
    wolf: null,     // per-hole Wolf picks
    dots: null,     // per-hole dot awards
    wolfBet: null,  // overrides / multipliers / presses
    updatedAt: now,
  }))
  // Re-sending to the app CLEARS the app-side hidden flag, so a game the app
  // previously deleted (hiddenApp=true) reappears in the app's list.
  await update(idxRef(uid, id), { name: meta?.name || meta?.gameId || 'Game', gameId: meta?.gameId || null, updatedAt: now, hiddenApp: false })
  return id
}

// ── Season mode (piggybacks on /ggGames + /memberGames; NO new nodes/rules) ──
// A season is a normal owner-scoped /ggGames record tagged meta.kind:'season'
// (+ seasonType), so it lives under the SAME rules as games and the companion
// app can read /ggGames/{id} later. The /memberGames index carries kind:'season'
// so the Season tab lists only seasons and Saved Games can filter them out.
function seasonPlayers(meta) {
  const out = []
  for (const t of meta?.teams || []) for (const p of t.players || []) {
    out.push({ playerId: p.playerId, name: p.name || '', courseHandicap: Number(p.ch) || 0, teamId: t.id })
  }
  return out
}

export async function saveMemberSeason(meta) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const now = Date.now()
  const rec = stripUndefinedDeep({
    ownerId: uid, clubId: CLUB_ID, brand: BRAND, status: 'active', origin: 'site', sharedWith: {},
    createdAt: now, updatedAt: now,
    meta: { ...meta, kind: 'season', createdAt: now, status: 'active' },
    players: seasonPlayers(meta), scores: {},
  })
  const node = push(ref(memberDb, 'ggGames'))
  const gameId = node.key
  await set(node, rec)
  await set(idxRef(uid, gameId), {
    role: 'owner', name: meta?.name || 'Season', gameId: 'season', kind: 'season',
    seasonType: meta?.seasonType || null, status: 'active', updatedAt: now,
  })
  return gameId
}

export async function updateMemberSeason(id, meta) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const now = Date.now()
  const prevMeta = (await get(ref(memberDb, `ggGames/${id}/meta`))).val() || {}
  await update(ref(memberDb, `ggGames/${id}`), stripUndefinedDeep({
    meta: { ...meta, kind: 'season', createdAt: prevMeta.createdAt || now, status: prevMeta.status || 'active' },
    brand: BRAND,
    players: seasonPlayers(meta), updatedAt: now,
  }))
  await update(idxRef(uid, id), {
    name: meta?.name || 'Season', kind: 'season', seasonType: meta?.seasonType || null, updatedAt: now,
  })
  return id
}

// ── Saved RESULTS (view/delete-only snapshots the companion app writes) ───────
// Separate nodes from the play games: /ggResults/{id} (full snapshot) +
// /memberResults/{uid}/{id} (own-only index). Rules for these deploy from the
// MAIN CompleteTourneyLive repo. Own-only list; delete removes BOTH nodes.
const resIdxRef = (uid, rid) => ref(memberDb, `memberResults/${uid}${rid ? '/' + rid : ''}`)

export function watchMyResults(cb) {
  const uid = getMemberId()
  if (!uid) { cb([]); return () => {} }
  return onValue(resIdxRef(uid), (s) => {
    const v = s.val() || {}
    cb(Object.entries(v)
      .map(([id, r]) => ({ id, ...(r || {}) }))
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)))
  })
}

export async function loadResult(id) {
  const s = await get(ref(memberDb, `ggResults/${id}`))
  return s.val() || null
}

export async function deleteResult(id) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  await remove(ref(memberDb, `ggResults/${id}`))
  await remove(resIdxRef(uid, id))
}
